// Interfaces for the Offline Access feature.
// See offline-access-frontend-walkthrough for the backend contract.
//
// Field shape mirrors the existing ticket-registration `/Forms` convention
// (see views/Admin/Forms/ManageForm.tsx): `type` is the numeric `FormType`
// enum, `options` is a string[] (only meaningful for MultiChoice/DropDown),
// and `required` is a boolean.
import { FormType } from "./interfaces-data";

// Every enum is serialized as an integer (confirmed against the live API,
// e.g. GET /OfflineAccess/Setup/1/{resourceId} for a Presentation). The
// walkthrough doc's string values (e.g. "Event") are outdated — ignore them.

export enum OfflineAccessResourceType {
  Event = 0,
  Presentation = 1,
  FileSharing = 2,
}

export enum OfflineAccessRestriction {
  None = 0,
  Whitelist = 1,
  Blacklist = 2,
}

export enum OfflineAccessDownloadOption {
  None = 0,
  Individual_Slide = 1,
  Full_Presentation = 2,
}

export enum OfflineAccessSystemField {
  None = 0,
  FullName = 1,
  Email = 2,
  Phone = 3,
}

export interface OfflineAccessField {
  fieldId?: string;
  label: string;
  type: FormType;
  required: boolean;
  sortOrder: number;
  systemField: OfflineAccessSystemField;
  options: string[];
}

export interface OfflineAccessSetupPayload {
  name: string;
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  teamId: string;
  enabled: boolean;
  publicLink: boolean;
  allowDirectMessage: boolean;
  requireOtp: boolean;
  linkCanExpire: boolean;
  expiresAt?: string | null;
  restriction: OfflineAccessRestriction;
  domains: string[];
  // Private-link flow: the explicit allow-list of recipient emails. Only
  // meaningful when `publicLink` is false; sent empty for public links.
  emails: string[];
  canDownload: OfflineAccessDownloadOption;
  fields: OfflineAccessField[];
  // Multi-link flow: present when UPDATING one existing shared-playlist link
  // (POST /OfflineAccess/Setup). Absent for the legacy single-setup upsert.
  settingId?: string;
}

// ---------------------------------------------------------------------------
// Multi-link (shared playlist) types.
// A playlist can have many shareable links, each its own OfflineAccessSetting
// identified by linkId (== settingId == id). These sit ALONGSIDE the legacy
// single-setup types above; nothing here removes the old flow.
// ---------------------------------------------------------------------------

// A document entry returned on a link object. Prefer `docsUrl` for rendering;
// never derive the friendlyId on the frontend.
export interface OfflineAccessLinkDoc {
  id: string;
  documentId: string;
  title?: string | null;
  type?: string | null;
  position: number;
  friendlyId: string;
  docsUrl: string;
}

// The rich link object returned by create + list. Extends the config payload
// shape with the backend-owned identifiers and document contract.
export interface OfflineAccessLink
  extends Omit<OfflineAccessSetupPayload, "name"> {
  id: string;
  name: string | null;
  linkId: string;
  friendlyId: string;
  dateCreated?: string | null;
  numberOfVisits?: number | null;
  linkQuery?: string;
  docsBaseUrl?: string | null;
  docs?: OfflineAccessLinkDoc[];
  resource?: OfflineAccessResourceSummary;
  publicUrl?: string;
}

// Body for POST /OfflineAccess/Links (create). Same shape as the setup payload
// minus settingId (create never carries one).
export type CreateOfflineAccessLinkPayload = Omit<
  OfflineAccessSetupPayload,
  "settingId"
>;

// Params for GET /OfflineAccess/Setup/{resourceType}/{resourceId}?linkId=
// (the "get one link" edit variant).
export interface GetOneOfflineAccessLinkParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  linkId: string;
  eventId?: string | null;
}

// Params for GET /OfflineAccess/Links/{resourceType}/{resourceId}
export interface ListOfflineAccessLinksParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  teamId?: string | null;
}

// Args for PUT /OfflineAccess/Links/{linkSettingId}/Access (enable/disable).
export interface SetLinkAccessArgs {
  linkSettingId: string;
  enabled: boolean;
  teamId?: string | null;
}

// Args for DELETE /OfflineAccess/Links/{settingId}
export interface DeleteOfflineAccessLinkArgs {
  linkSettingId: string;
  teamId?: string | null;
}

export interface OfflineAccessResourceSummary {
  title: string;
  friendlyId?: string;
  sessionType?: number;
  eventStatus?: number | null;
  presentationStatus?: number | null;
  ownerId?: string | null;
  ownerName?: string | null;
}

export interface OfflineAccessSetupResponse
  extends Omit<OfflineAccessSetupPayload, "name"> {
  id?: string;
  name?: string | null;
  friendlyId?: string;
  linkQuery?: string;
  docsBaseUrl?: string | null;
  docs?: OfflineAccessLinkDoc[];
  resource?: OfflineAccessResourceSummary;
  publicUrl?: string;
}

export interface GetOfflineAccessSetupParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
}

export interface OfflineAccessAnswer {
  fieldId: string;
  label: string;
  response: string;
}

export interface OfflineAccessRequestPayload {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  fullName: string;
  email: string;
  answers?: OfflineAccessAnswer[];
  // Multi-link flow: identifies which shared-playlist link is being accessed.
  settingId?: string;
}

export interface OfflineAccessRequestResponse {
  requiresOtp: boolean;
  reference?: string;
  email?: string;
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  // Present when OTP is not required.
  presentaConnectionId?: string;
  next?: OfflineAccessNext;
  // Echoed back on the multi-link flow.
  settingId?: string;
  linkId?: string;
}

export interface OfflineAccessVerifyPayload {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  email: string;
  reference: string;
  otp: string;
  // Multi-link flow: identifies which shared-playlist link is being accessed.
  settingId?: string;
}

export interface OfflineAccessNext {
  presentationUrl: string | null;
  eventUrl: string | null;
}

export interface OfflineAccessVerifyResponse {
  requiresOtp: boolean;
  verified: boolean;
  presentaConnectionId: string;
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  next?: OfflineAccessNext;
  // Echoed back on the multi-link flow.
  settingId?: string;
  linkId?: string;
  // Auth block, mirroring the login endpoints (ValidateOTPServiceResponse).
  // Present only when the verified email maps to a real account — the offline
  // access OTP is the same OTP used for login, so a successful verify can log
  // the attendee in. Absent for guests without an account.
  token?: string;
  fullName?: string;
  email?: string;
  phone?: string | null;
  expiresIn?: string;
  roles?: string;
  country?: string;
}

export interface OfflineAccessQuestionsParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  presentaConnectionId?: string;
  slideId?: string;
  // Multi-link flow: the shared-playlist link the visitor is interacting from.
  linkId?: string;
}

export interface OfflineAccessQuestionPayload {
  presentationId: string;
  presentaConnectionId: string;
  slideId: string;
  question: string;
  questionId: string | null;
  // Multi-link flow: sent in the body as settingId.
  settingId?: string;
}

export interface OfflineAccessQuestionAnswerPayload {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  questionId: string;
  answer: string;
}

// Identifies a single question to vote on. The vote endpoints are REST-only —
// the offline page has no SignalR connection, so votes go over HTTP.
export interface OfflineAccessQuestionVoteParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  questionId: string;
  presentaConnectionId: string;
  // Multi-link flow: settingId in the body (POST) / linkId in the query (DELETE).
  linkId?: string;
}

// Payload for sending (POST) a direct message to the link owner.
export interface OfflineAccessDirectMessagePayload {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  presentaConnectionId: string;
  message: string;
  // Multi-link flow: sent in the body as settingId.
  settingId?: string;
}

// Params for fetching (GET) the direct-message thread. Same path/identity as
// the POST, minus the message body.
export interface OfflineAccessDirectMessageParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  presentaConnectionId: string;
  // Multi-link flow: linkId in the query.
  linkId?: string;
}

// A single direct-message record returned by the GET endpoint.
export interface OfflineAccessDirectMessageItem {
  id: string;
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId: string | null;
  presentaConnectionId: string;
  senderEmail: string;
  senderName: string;
  recipientUserId: string | null;
  recipientEmail: string | null;
  message: string;
  dateSent: string;
}
